import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { setFullViewportWidthWithMaxPageWidthStyles, theme } from "shared/lib/styles";

import { ArticleRenderContainer } from "scmp-app/components/article/article-render/styles";

/**
 * Always-reserved 202px slot for the infinite-scroll trigger.
 *
 * 1. Fixed 202px of vertical space, permanently in-flow, never collapsed
 *    (no display:none, no visibility toggle, no children) — show/hide of
 *    this slot never shifts document flow.
 *
 * 2. `overflow-anchor: none` stops Chrome's scroll-anchoring from picking
 *    this slot as the anchor node. Without it, when the next article
 *    inserts above, Chrome anchors here and readjusts scrollTop — which
 *    DevTools attributes as CLS on the slot. Opting out pushes the anchor
 *    higher up the document, nothing inside the slot moves, CLS stays 0.
 *
 * The height is what the spinner used to occupy; the slot is now
 * deliberately empty and only exists as a stable, anchor-ineligible
 * IntersectionObserver trigger box.
 */
export const LoadingSlot = styled.div<{ $isHidden?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  block-size: 202px;

  overflow-anchor: none;

  /* Collapse must ride along with the footer swap: when pagination ends,
     the footer (+ its reserved height) appears in the same React commit,
     so the fold boundary jumps down and removing 202px here shifts
     nothing visible. A later, standalone collapse (e.g. triggered by the
     scroll observer after the user settles at the bottom) has no room to
     compensate and re-introduces CLS — so the gate is only
     "pagination exhausted", never a viewport check. */
  ${props =>
    props.$isHidden &&
    css`
      display: none;
    `}
`;

export const Divider = styled.div`
  position: relative;
  z-index: 2;

  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;

  block-size: 1px;

  background-color: #dddddd;

  /* Same treatment as the trigger slot: the divider between the current
     article and the next inserted article is pushed down by each insert,
     and scroll anchoring was attributing the resulting compensation as
     CLS on this element. Opt out so the anchor stays on content the user
     is reading. */
  overflow-anchor: none;

  &::before,
  &::after {
    content: "";

    display: block;
    flex: none;

    inline-size: 30px;
    block-size: 5px;

    outline: #ffffff solid 4px;
  }

  &::before {
    background-color: #ffca05;
  }

  &::after {
    background-color: #001246;
  }

  &:last-child {
    display: none;
  }

  ${theme.breakpoints.only("mobile")} {
    ${setFullViewportWidthWithMaxPageWidthStyles()}
  }
`;

export const Item = styled.div`
  position: relative;

  &:first-of-type ${ArticleRenderContainer} > *:first-child {
    padding-block-start: 0;
  }
`;
